// Drag-to-fill support for AG Grid Community.
//
// Mirrors AG Grid's Enterprise range-selection interaction: dragging across
// cells selects one contiguous rectangle, the handle sits on its bottom-right
// cell, and dragging the handle repeats the selected row or column pattern.
// Value changes go through RowNode#setDataValue so the grid's existing setters,
// change events, validation, totals, and autosave behavior continue to run.

const HANDLE_CLASS = "ag-fill-handle"
const PREVIEW_CLASS = "ag-cell--fill-preview"
const OUTLINE_CLASS = "ag-fill-range-outline"
const PREVIEW_EDGE_CLASSES = {
  top: "ag-selection-fill-top",
  right: "ag-selection-fill-right",
  bottom: "ag-selection-fill-bottom",
  left: "ag-selection-fill-left"
}
const RANGE_SELECTED_CLASS = "ag-cell-range-selected"
const RANGE_COUNT_CLASS = "ag-cell-range-selected-1"
const RANGE_SINGLE_CLASS = "ag-cell-range-single-cell"
const RANGE_EDGE_CLASSES = {
  top: "ag-cell-range-top",
  right: "ag-cell-range-right",
  bottom: "ag-cell-range-bottom",
  left: "ag-cell-range-left"
}
const RANGE_CLASSES = [
  RANGE_SELECTED_CLASS,
  RANGE_COUNT_CLASS,
  RANGE_SINGLE_CLASS,
  ...Object.values(RANGE_EDGE_CLASSES)
]

export function install(controller, dom = gridDom) {
  const state = {
    controller,
    dom,
    handle: null,
    selection: null,
    source: null,
    destinations: [],
    stopWatchingDrag: null,
    stopWatchingSelectionDrag: null
  }

  state.onFocus = (event) => {
    if (state.stopWatchingSelectionDrag) return

    const cell = focusedEditableCell(controller, event)
    setSelection(state, cell ? selectionBetween(controller, cell, cell) : null)
    scheduleHandle(state)
  }
  state.onEditingStarted = () => {
    cancelScheduledHandle(state)
    stopSelectionDrag(state)
    stopDrag(state)
    removeHandle(state)
  }
  state.onEditingStopped = () => scheduleHandle(state)
  state.onValueChanged = () => scheduleHandle(state)
  state.onModelUpdated = () => scheduleHandle(state)
  state.onClickAway = () => clearSelection(state)

  controller.gridApi.addEventListener("cellFocused", state.onFocus)
  controller.gridApi.addEventListener("cellEditingStarted", state.onEditingStarted)
  controller.gridApi.addEventListener("cellEditingStopped", state.onEditingStopped)
  controller.gridApi.addEventListener("cellValueChanged", state.onValueChanged)
  controller.gridApi.addEventListener("modelUpdated", state.onModelUpdated)
  state.stopWatchingSelection = dom.watchSelection(controller, (event) => beginSelection(state, event))
  state.stopWatchingClickAway = dom.watchClickAway(controller, state.onClickAway)
  controller._fillHandle = state
}

export function uninstall(controller) {
  const state = controller._fillHandle

  cancelScheduledHandle(state)
  stopSelectionDrag(state)
  stopDrag(state)
  removeHandle(state)
  state.stopWatchingSelection?.()
  state.stopWatchingClickAway?.()
  state.dom.clearSelection(controller)

  controller.gridApi.removeEventListener("cellFocused", state.onFocus)
  controller.gridApi.removeEventListener("cellEditingStarted", state.onEditingStarted)
  controller.gridApi.removeEventListener("cellEditingStopped", state.onEditingStopped)
  controller.gridApi.removeEventListener("cellValueChanged", state.onValueChanged)
  controller.gridApi.removeEventListener("modelUpdated", state.onModelUpdated)
  delete controller._fillHandle
}

function scheduleHandle(state, focused) {
  cancelScheduledHandle(state)
  state.cancelScheduledHandle = state.dom.afterGridUpdate(state.controller, () => {
    state.cancelScheduledHandle = null
    showHandle(state, focused)
  })
}

function cancelScheduledHandle(state) {
  state.cancelScheduledHandle?.()
  state.cancelScheduledHandle = null
}

function showHandle(state, focused) {
  stopDrag(state)
  removeHandle(state)

  if (!state.selection) {
    const cell = focusedEditableCell(state.controller, focused)
    setSelection(state, cell ? selectionBetween(state.controller, cell, cell) : null)
  } else {
    state.dom.showSelection(state.controller, state.selection)
  }

  attachSelectionHandle(state)
}

function attachSelectionHandle(state) {
  const selection = state.selection
  if (!selection) return

  const source = selection.cells.at(-1)
  state.handle = state.dom.attachHandle(
    state.controller,
    source,
    (event) => beginDrag(state, selection, event),
    (event) => fillColumn(state, selection, event)
  )
}

function removeHandle(state) {
  state.handle?.remove()
  state.handle = null
}

function beginSelection(state, event) {
  if (event.button !== 0) return

  const anchor = state.dom.cellAtPoint(state.controller, event.clientX, event.clientY)
  const selection = anchor && selectionBetween(state.controller, anchor, anchor)
  if (!selection) return

  cancelScheduledHandle(state)
  stopSelectionDrag(state)
  stopDrag(state)
  removeHandle(state)
  setSelection(state, selection)

  const startX = event.clientX
  const startY = event.clientY
  state.stopWatchingSelectionDrag = state.dom.watchDrag({
    move: (moveEvent) => {
      const deltaX = Math.abs(moveEvent.clientX - startX)
      const deltaY = Math.abs(moveEvent.clientY - startY)
      if (Math.max(deltaX, deltaY) < 4) return

      const target = state.dom.cellAtPoint(state.controller, moveEvent.clientX, moveEvent.clientY)
      const nextSelection = target && selectionBetween(state.controller, anchor, target)
      if (!nextSelection) return

      removeHandle(state)
      setSelection(state, nextSelection)
    },
    end: () => {
      stopSelectionDrag(state)
      attachSelectionHandle(state)
    },
    cancel: () => {
      stopSelectionDrag(state)
      attachSelectionHandle(state)
    }
  }, state.controller)
}

function stopSelectionDrag(state) {
  state.stopWatchingSelectionDrag?.()
  state.stopWatchingSelectionDrag = null
}

function setSelection(state, selection) {
  state.selection = selection
  if (selection) {
    state.dom.showSelection(state.controller, selection)
  } else {
    state.dom.clearSelection(state.controller)
  }
}

function clearSelection(state) {
  cancelScheduledHandle(state)
  stopSelectionDrag(state)
  stopDrag(state)
  removeHandle(state)
  setSelection(state, null)
}

function selectionBetween(controller, start, finish) {
  const api = controller.gridApi
  const displayedColumns = api.getAllDisplayedColumns()
  const startColumnIndex = displayedColumns.indexOf(start.column)
  const finishColumnIndex = displayedColumns.indexOf(finish.column)
  if (startColumnIndex < 0 || finishColumnIndex < 0) return null

  const startRowIndex = Math.min(start.rowIndex, finish.rowIndex)
  const endRowIndex = Math.max(start.rowIndex, finish.rowIndex)
  const firstColumnIndex = Math.min(startColumnIndex, finishColumnIndex)
  const lastColumnIndex = Math.max(startColumnIndex, finishColumnIndex)
  const columns = displayedColumns.slice(firstColumnIndex, lastColumnIndex + 1)
  const cells = []

  for (let rowIndex = startRowIndex; rowIndex <= endRowIndex; rowIndex += 1) {
    const node = api.getDisplayedRowAtIndex(rowIndex)
    for (const column of columns) {
      cells.push({ rowIndex, column, node })
    }
  }

  if (cells.length === 0 || !cells.every((cell) => isEditableDataCell(controller, cell))) return null

  return { startRowIndex, endRowIndex, columns, cells }
}

function beginDrag(state, source, event) {
  if (event.button !== 0) return

  event.preventDefault()
  event.stopPropagation()
  stopSelectionDrag(state)

  state.source = source
  state.startX = event.clientX
  state.startY = event.clientY
  state.axis = null
  state.destinations = []
  state.stopWatchingDrag = state.dom.watchDrag({
    move: (moveEvent) => updateDrag(state, moveEvent),
    end: () => finishDrag(state),
    cancel: () => stopDrag(state)
  }, state.controller)
}

function fillColumn(state, source, event) {
  event.preventDefault()
  event.stopPropagation()
  stopDrag(state)

  const api = state.controller.gridApi
  const lastRowIndex = api.getDisplayedRowCount() - 1
  if (lastRowIndex <= source.endRowIndex) return

  const target = { rowIndex: lastRowIndex, column: source.columns.at(-1) }
  const destinations = destinationCells(state.controller, source, target, "vertical")
    .filter(({ node }) => hasRowData(node?.data))

  copyToDestinations(state.controller, source, destinations)
  expandSelection(state, source, destinations)
}

function updateDrag(state, event) {
  if (!state.axis) {
    const deltaX = Math.abs(event.clientX - state.startX)
    const deltaY = Math.abs(event.clientY - state.startY)
    // Ignore pointer jitter until movement reaches four CSS pixels, then lock to one axis.
    if (Math.max(deltaX, deltaY) < 4) return
    state.axis = deltaX >= deltaY ? "horizontal" : "vertical"
  }

  const target = state.dom.cellAtPoint(state.controller, event.clientX, event.clientY)
  state.destinations = target ? destinationCells(state.controller, state.source, target, state.axis) : []
  state.dom.showPreview(state.controller, state.destinations, state.source)
}

function finishDrag(state) {
  const destinations = state.destinations
  const source = state.source
  stopDrag(state)

  if (destinations.length === 0) return

  copyToDestinations(state.controller, source, destinations)
  expandSelection(state, source, destinations)
}

function expandSelection(state, source, destinations) {
  const cells = [...source.cells, ...destinations]
  const rows = cells.map(({ rowIndex }) => rowIndex)
  const displayedColumns = state.controller.gridApi.getAllDisplayedColumns()
  const columnIndexes = cells.map(({ column }) => displayedColumns.indexOf(column))
  const start = {
    rowIndex: Math.min(...rows),
    column: displayedColumns[Math.min(...columnIndexes)]
  }
  const finish = {
    rowIndex: Math.max(...rows),
    column: displayedColumns[Math.max(...columnIndexes)]
  }
  const expanded = selectionBetween(state.controller, start, finish)

  removeHandle(state)
  setSelection(state, expanded || source)
  attachSelectionHandle(state)
}

function stopDrag(state) {
  state.stopWatchingDrag?.()
  state.stopWatchingDrag = null
  state.dom.clearPreview(state.controller)
  state.source = null
  state.destinations = []
  state.axis = null
}

function focusedEditableCell(controller, focused) {
  const api = controller.gridApi
  if (api.getEditingCells().length > 0) return null

  focused ||= api.getFocusedCell()
  if (!focused || focused.rowPinned) return null

  const node = api.getDisplayedRowAtIndex(focused.rowIndex)
  const cell = { rowIndex: focused.rowIndex, column: focused.column, node }
  return isEditableDataCell(controller, cell) ? cell : null
}

function destinationCells(controller, source, target, axis) {
  const api = controller.gridApi
  const cells = []

  if (axis === "horizontal") {
    const columns = api.getAllDisplayedColumns()
    const sourceStart = columns.indexOf(source.columns[0])
    const sourceEnd = columns.indexOf(source.columns.at(-1))
    const targetIndex = columns.indexOf(target.column)
    if (sourceStart < 0 || sourceEnd < 0 || targetIndex < 0) return cells

    let destinationColumns = []
    if (targetIndex < sourceStart) {
      destinationColumns = columns.slice(targetIndex, sourceStart)
    } else if (targetIndex > sourceEnd) {
      destinationColumns = columns.slice(sourceEnd + 1, targetIndex + 1)
    }

    for (let rowIndex = source.startRowIndex; rowIndex <= source.endRowIndex; rowIndex += 1) {
      const node = api.getDisplayedRowAtIndex(rowIndex)
      for (const column of destinationColumns) cells.push({ rowIndex, column, node })
    }
  } else {
    let startRowIndex
    let endRowIndex
    if (target.rowIndex < source.startRowIndex) {
      startRowIndex = target.rowIndex
      endRowIndex = source.startRowIndex - 1
    } else if (target.rowIndex > source.endRowIndex) {
      startRowIndex = source.endRowIndex + 1
      endRowIndex = target.rowIndex
    } else {
      return cells
    }

    for (let rowIndex = startRowIndex; rowIndex <= endRowIndex; rowIndex += 1) {
      const node = api.getDisplayedRowAtIndex(rowIndex)
      for (const column of source.columns) cells.push({ rowIndex, column, node })
    }
  }

  return cells
}

function isEditableDataCell(controller, { rowIndex, column, node }) {
  if (!node || node.rowPinned || String(node.data?.id).startsWith("__")) return false

  const colDef = column.getColDef()
  if (!colDef.field && !colDef.valueSetter) return false
  if (!fillable(controller, colDef.field)) return false

  const params = {
    api: controller.gridApi,
    column,
    colDef,
    data: node.data,
    node,
    rowIndex,
    value: cellValue(controller.gridApi, node, column)
  }
  const editable = typeof colDef.editable === "function" ? colDef.editable(params) : colDef.editable
  return editable !== false
}

function fillable(controller, field) {
  const configured = controller.columnDefsValue.find((colDef) => colDef.field === field)
  return configured?.fillable !== false
}

function copyToDestinations(controller, source, destinations) {
  const api = controller.gridApi
  let filledBlankRow = false

  for (const destination of destinations) {
    if (!isEditableDataCell(controller, destination)) continue

    const wasBlankRow = !hasRowData(destination.node.data)
    const sourceCell = sourceCellForDestination(api, source, destination)
    const value = cellValue(api, sourceCell.node, sourceCell.column)
    const sourceValueField = valueField(sourceCell.column)
    const backingValue = sourceValueField ? sourceCell.node.data[sourceValueField] : value
    const changed = setCellValue(controller, destination, value, backingValue)

    if (changed && wasBlankRow) filledBlankRow = true
  }

  if (controller.addableValue && filledBlankRow) {
    api.applyTransaction({ add: [{ id: controller.nextEmptyId() }] })
  }
}

function sourceCellForDestination(api, source, destination) {
  const columns = api.getAllDisplayedColumns()
  const sourceColumnIndex = columns.indexOf(source.columns[0])
  const destinationColumnIndex = columns.indexOf(destination.column)
  const rowOffset = positiveModulo(
    destination.rowIndex - source.startRowIndex,
    source.endRowIndex - source.startRowIndex + 1
  )
  const columnOffset = positiveModulo(destinationColumnIndex - sourceColumnIndex, source.columns.length)
  const rowIndex = source.startRowIndex + rowOffset

  return {
    rowIndex,
    column: source.columns[columnOffset],
    node: api.getDisplayedRowAtIndex(rowIndex)
  }
}

function positiveModulo(value, divisor) {
  return ((value % divisor) + divisor) % divisor
}

function setCellValue(controller, cell, value, backingValue) {
  const { column, node } = cell
  const api = controller.gridApi
  const destinationValueField = valueField(column)
  const oldValue = cellValue(api, node, column)
  const sameValue = Object.is(oldValue, value)
  let oldBackingValue
  let backingChanged = false

  // Searchable editors keep a display label in the column field and an ID in
  // valueField. Set the ID before setDataValue so the normal change event sees
  // the complete row, then roll it back if a destination setter rejects value.
  if (destinationValueField) {
    oldBackingValue = node.data[destinationValueField]
    backingChanged = !Object.is(oldBackingValue, backingValue)
    node.data[destinationValueField] = backingValue
  }

  const valueChanged = node.setDataValue(column, value, "fill")
  if (!valueChanged && !sameValue) {
    if (destinationValueField) node.data[destinationValueField] = oldBackingValue
    return false
  }

  if (valueChanged && backingChanged) {
    controller.syncRowToDOM(node.data.id, { [destinationValueField]: backingValue })
  }

  // AG Grid does not emit cellValueChanged when the display value is equal.
  // Use the controller's existing programmatic update pathway when only the
  // generic backing field changed so DOM sync and autosave still occur.
  if (!valueChanged && backingChanged) {
    controller.updateRow(node.data.id, { [destinationValueField]: backingValue })
  }

  return valueChanged || backingChanged
}

function valueField(column) {
  return column.getColDef().cellEditorParams?.valueField
}

function hasRowData(data) {
  return Object.entries(data || {}).some(([field, value]) => {
    return field !== "id" && field !== "recordId" && !field.startsWith("_") &&
      value !== undefined && value !== null && String(value).trim() !== ""
  })
}

function cellValue(api, node, column) {
  return api.getCellValue({ rowNode: node, colKey: column })
}

// All AG Grid DOM knowledge is kept in this adapter. The fill behavior above
// works only with row indexes and Column/RowNode objects.
const gridDom = {
  afterGridUpdate(controller, callback) {
    const ownerWindow = controller.element.ownerDocument.defaultView
    const frame = ownerWindow.requestAnimationFrame(callback)
    return () => ownerWindow.cancelAnimationFrame(frame)
  },

  watchSelection(controller, onPointerDown) {
    controller.containerTarget.addEventListener("pointerdown", onPointerDown)
    return () => controller.containerTarget.removeEventListener("pointerdown", onPointerDown)
  },

  watchClickAway(controller, onClickAway) {
    const ownerDocument = controller.element.ownerDocument
    const onPointerDown = (event) => {
      if (!controller.containerTarget.contains(event.target)) onClickAway()
    }
    ownerDocument.addEventListener("pointerdown", onPointerDown)
    return () => ownerDocument.removeEventListener("pointerdown", onPointerDown)
  },

  attachHandle(controller, cell, onPointerDown, onDoubleClick) {
    const cellElement = findCellElement(controller, cell)
    if (!cellElement) return null

    const handle = controller.element.ownerDocument.createElement("div")
    handle.className = HANDLE_CLASS
    handle.setAttribute("aria-hidden", "true")
    handle.addEventListener("pointerdown", onPointerDown)
    handle.addEventListener("dblclick", onDoubleClick)
    cellElement.appendChild(handle)
    return handle
  },

  watchDrag(handlers, controller) {
    const ownerDocument = controller.element.ownerDocument
    ownerDocument.addEventListener("pointermove", handlers.move)
    ownerDocument.addEventListener("pointerup", handlers.end, { once: true })
    ownerDocument.addEventListener("pointercancel", handlers.cancel, { once: true })

    return () => {
      ownerDocument.removeEventListener("pointermove", handlers.move)
      ownerDocument.removeEventListener("pointerup", handlers.end)
      ownerDocument.removeEventListener("pointercancel", handlers.cancel)
    }
  },

  cellAtPoint(controller, x, y) {
    const element = controller.element.ownerDocument.elementFromPoint(x, y)
    const cellElement = element?.closest(".ag-cell")
    const rowElement = cellElement?.closest(".ag-row")
    if (!cellElement || !rowElement || !controller.containerTarget.contains(cellElement)) return null
    if (rowElement.classList.contains("ag-row-pinned") || rowElement.closest(".ag-floating-top, .ag-floating-bottom")) return null

    const rowIndex = Number(rowElement.getAttribute("row-index"))
    const colId = cellElement.getAttribute("col-id")
    const column = controller.gridApi.getAllDisplayedColumns().find((candidate) => candidate.getColId() === colId)
    return Number.isInteger(rowIndex) && column ? { rowIndex, column } : null
  },

  showSelection(controller, selection) {
    this.clearSelection(controller)

    const firstColumn = selection.columns[0]
    const lastColumn = selection.columns.at(-1)
    const singleCell = selection.cells.length === 1

    for (const cell of selection.cells) {
      const element = findCellElement(controller, cell)
      if (!element) continue

      element.classList.add(RANGE_SELECTED_CLASS, RANGE_COUNT_CLASS)
      element.setAttribute("aria-selected", "true")

      if (singleCell) {
        element.classList.add(RANGE_SINGLE_CLASS)
      } else {
        if (cell.rowIndex === selection.startRowIndex) element.classList.add(RANGE_EDGE_CLASSES.top)
        if (cell.rowIndex === selection.endRowIndex) element.classList.add(RANGE_EDGE_CLASSES.bottom)
        if (cell.column === firstColumn) element.classList.add(RANGE_EDGE_CLASSES.left)
        if (cell.column === lastColumn) element.classList.add(RANGE_EDGE_CLASSES.right)
      }
    }
  },

  clearSelection(controller) {
    controller.containerTarget.querySelectorAll(`.${RANGE_SELECTED_CLASS}`).forEach((element) => {
      element.classList.remove(...RANGE_CLASSES)
      element.removeAttribute("aria-selected")
    })
  },

  showPreview(controller, cells, source) {
    this.clearPreview(controller)

    const cellElements = cells.map((cell) => ({ cell, element: findCellElement(controller, cell) }))
      .filter(({ element }) => element)
    if (cellElements.length === 0) return

    const rows = cells.map(({ rowIndex }) => rowIndex)
    const displayedColumns = controller.gridApi.getAllDisplayedColumns()
    const columnIndexes = cells.map(({ column }) => displayedColumns.indexOf(column))
    const firstRow = Math.min(...rows)
    const lastRow = Math.max(...rows)
    const firstColumn = Math.min(...columnIndexes)
    const lastColumn = Math.max(...columnIndexes)
    const sourceFirstColumn = displayedColumns.indexOf(source.columns[0])
    const sourceLastColumn = displayedColumns.indexOf(source.columns.at(-1))
    const joinsSourceAtTop = source.endRowIndex < firstRow
    const joinsSourceAtRight = sourceLastColumn < firstColumn
    const joinsSourceAtBottom = source.startRowIndex > lastRow
    const joinsSourceAtLeft = sourceFirstColumn > lastColumn

    for (const { cell, element } of cellElements) {
      const columnIndex = displayedColumns.indexOf(cell.column)
      element.classList.add(PREVIEW_CLASS)
      if (cell.rowIndex === firstRow && !joinsSourceAtTop) element.classList.add(PREVIEW_EDGE_CLASSES.top)
      if (cell.rowIndex === lastRow && !joinsSourceAtBottom) element.classList.add(PREVIEW_EDGE_CLASSES.bottom)
      if (columnIndex === firstColumn && !joinsSourceAtRight) element.classList.add(PREVIEW_EDGE_CLASSES.left)
      if (columnIndex === lastColumn && !joinsSourceAtLeft) element.classList.add(PREVIEW_EDGE_CLASSES.right)
    }

    const elements = cellElements.map(({ element }) => element)
    const rects = elements.map((element) => element.getBoundingClientRect())
    const left = Math.min(...rects.map((rect) => rect.left))
    const top = Math.min(...rects.map((rect) => rect.top))
    const right = Math.max(...rects.map((rect) => rect.right))
    const bottom = Math.max(...rects.map((rect) => rect.bottom))
    const outline = controller.element.ownerDocument.createElement("div")
    outline.className = OUTLINE_CLASS
    outline.style.left = `${left}px`
    outline.style.top = `${top}px`
    outline.style.width = `${right - left}px`
    outline.style.height = `${bottom - top}px`

    const sourceElements = source.cells.map((cell) => findCellElement(controller, cell)).filter(Boolean)
    const sourceRects = sourceElements.map((element) => element.getBoundingClientRect())
    if (sourceRects.length > 0) {
      const sourceLeft = Math.min(...sourceRects.map((rect) => rect.left))
      const sourceTop = Math.min(...sourceRects.map((rect) => rect.top))
      const sourceRight = Math.max(...sourceRects.map((rect) => rect.right))
      const sourceBottom = Math.max(...sourceRects.map((rect) => rect.bottom))
      if (sourceBottom <= top) outline.style.borderTopWidth = "0"
      if (sourceTop >= bottom) outline.style.borderBottomWidth = "0"
      if (sourceRight <= left) outline.style.borderLeftWidth = "0"
      if (sourceLeft >= right) outline.style.borderRightWidth = "0"
    }

    controller.element.ownerDocument.body.appendChild(outline)
    controller._fillHandle.previewOutline = outline
  },

  clearPreview(controller) {
    controller.containerTarget.querySelectorAll(`.${PREVIEW_CLASS}`).forEach((element) => {
      element.classList.remove(PREVIEW_CLASS, ...Object.values(PREVIEW_EDGE_CLASSES))
    })
    controller._fillHandle.previewOutline?.remove()
    controller._fillHandle.previewOutline = null
  }
}

function findCellElement(controller, { rowIndex, column }) {
  const colId = column.getColId()
  return Array.from(controller.containerTarget.querySelectorAll(".ag-cell")).find((element) => {
    const rowElement = element.closest(".ag-row")
    return element.getAttribute("col-id") === colId &&
      Number(rowElement?.getAttribute("row-index")) === rowIndex &&
      !rowElement?.classList.contains("ag-row-pinned")
  })
}
